import { useEffect, useMemo, useState } from "react";
import { api, AUD } from "../lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Legend } from "recharts";
import { toast } from "sonner";
import { ArrowClockwise, Sliders, Scales, ChatCircle } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { isDemoMode } from "../lib/demoStore";

export default function Retirement() {
    const [scenarios, setScenarios] = useState([]);
    const [defaults, setDefaults] = useState(null);
    const [selected, setSelected] = useState(null);
    const [sim, setSim] = useState(null);
    const [form, setForm] = useState({ starting_assets: 0, annual_spending: 0, return_rate: 0.06, inflation: 0.03, years: 40 });

    useEffect(() => {
        Promise.all([
            api.get("/retirement/scenarios"),
            api.get("/retirement/defaults"),
        ]).then(([s, d]) => {
            setScenarios(s.data);
            setDefaults(d.data);
            const initialForm = {
                starting_assets: d.data.starting_assets,
                annual_spending: d.data.annual_spending,
                return_rate: d.data.return_rate,
                inflation: d.data.inflation,
                years: d.data.years,
            };
            setForm(initialForm);
            // auto-run using assumption-based defaults
            api.post("/retirement/simulate", initialForm).then(r => setSim(r.data));
        });
    }, []);

    const pick = async (s) => {
        setSelected(s);
        const body = {
            starting_assets: defaults?.starting_assets || s.starting_assets,
            annual_spending: s.annual_spending,
            return_rate: s.return_rate,
            inflation: s.inflation,
            years: defaults?.years || 40,
        };
        setForm(body);
        const { data } = await api.post("/retirement/simulate", body);
        setSim(data);
    };

    const runCustom = async () => {
        const { data } = await api.post("/retirement/simulate", form);
        setSim(data);
        setSelected(null);
    };

    const resetFromAssumptions = async () => {
        const { data } = await api.get("/retirement/defaults");
        setDefaults(data);
        const f = {
            starting_assets: data.starting_assets,
            annual_spending: data.annual_spending,
            return_rate: data.return_rate,
            inflation: data.inflation,
            years: data.years,
        };
        setForm(f);
        const sim2 = await api.post("/retirement/simulate", f);
        setSim(sim2.data);
        setSelected(null);
        toast.success("Reloaded from Assumptions page");
    };

    return (
        <div data-testid="retirement-page">
            <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Retirement Planner</div>
                    <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">Will the money last?</h1>
                    {defaults && (
                        <p className="text-sm mt-3 max-w-2xl" style={{ color: "var(--muted)" }}>
                            Starting position = investable financial assets from{" "}
                            <strong>{isDemoMode() ? "Super + SMSF" : "Pendaz + SMSF"}</strong> only (excludes personal home,
                            vehicles and loans). Other values pulled from your{" "}
                            <strong>Assumptions</strong> page. Change either and reload.
                        </p>
                    )}
                </div>
                <button className="btn-ghost flex items-center gap-2 text-sm"
                        onClick={resetFromAssumptions}
                        data-testid="retire-reset-btn">
                    <ArrowClockwise size={14} /> Reload from Assumptions
                </button>
            </div>

            {defaults && (
                <div className="hairline bg-white p-5 rounded mb-8 flex flex-wrap gap-6 items-center"
                     data-testid="retire-defaults-card">
                    <Sliders size={18} style={{ color: "var(--muted)" }} />
                    <div>
                        <div className="label-eyebrow">Starting assets</div>
                        <div className="font-mono-num text-sm mt-1">{AUD(defaults.starting_assets)}</div>
                    </div>
                    <div>
                        <div className="label-eyebrow">Annual spend</div>
                        <div className="font-mono-num text-sm mt-1">{AUD(defaults.annual_spending)}</div>
                    </div>
                    <div>
                        <div className="label-eyebrow">Return</div>
                        <div className="font-mono-num text-sm mt-1">{(defaults.return_rate * 100).toFixed(2)}%</div>
                    </div>
                    <div>
                        <div className="label-eyebrow">Inflation</div>
                        <div className="font-mono-num text-sm mt-1">{(defaults.inflation * 100).toFixed(2)}%</div>
                    </div>
                    <div>
                        <div className="label-eyebrow">Planning age</div>
                        <div className="font-mono-num text-sm mt-1">{defaults.planning_age}</div>
                    </div>
                </div>
            )}

            <div className="grid md:grid-cols-4 gap-4 mb-6" data-testid="scenarios">
                {scenarios.map(s => (
                    <button key={s.id}
                            data-testid={`scenario-${s.scenario.replace(/\s+/g, '-').toLowerCase()}`}
                            onClick={() => pick(s)}
                            className={`hairline p-5 rounded text-left transition-colors ${
                                selected?.id === s.id
                                    ? "bg-[var(--eucalyptus)] text-[var(--bone)]"
                                    : "bg-white hover:bg-black/5"
                            }`}>
                        <div className="label-eyebrow"
                             style={selected?.id === s.id ? { color: "rgba(255,255,255,0.75)" } : {}}>
                            {s.scenario}
                        </div>
                        <div className="font-serif text-2xl mt-3 font-mono-num">
                            {AUD(s.annual_spending)}
                            <span className="text-xs opacity-75"> / yr</span>
                        </div>
                        <div className="text-[10px] uppercase tracking-widest mt-2 opacity-75">
                            Status: {s.status}
                        </div>
                    </button>
                ))}
            </div>

            <div className="hairline bg-white p-8 rounded mb-6">
                <div className="label-eyebrow mb-4">Custom Model</div>
                <div className="grid md:grid-cols-5 gap-4 mb-4">
                    {[
                        { k: "starting_assets", label: "Starting Assets", step: 10000 },
                        { k: "annual_spending", label: "Annual Spend", step: 5000 },
                        { k: "return_rate", label: "Return (%)", step: 0.01, isPct: true },
                        { k: "inflation", label: "Inflation (%)", step: 0.005, isPct: true },
                        { k: "years", label: "Years", step: 1 },
                    ].map(f => (
                        <div key={f.k}>
                            <div className="label-eyebrow mb-2">{f.label}</div>
                            <input type="number" step={f.step}
                                   value={f.isPct ? form[f.k] * 100 : form[f.k]}
                                   onChange={e => setForm(v => ({ ...v, [f.k]: f.isPct ? Number(e.target.value) / 100 : Number(e.target.value) }))}
                                   data-testid={`retire-input-${f.k}`}
                                   className="w-full border border-[var(--line)] px-3 py-2 rounded text-sm font-mono-num" />
                        </div>
                    ))}
                </div>
                <button onClick={runCustom} className="btn-primary text-sm" data-testid="retire-run">
                    Run Simulation
                </button>
            </div>

            {sim && (
                <div className="hairline bg-white p-8 rounded" data-testid="retire-result">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <div className="label-eyebrow">Projected Balance</div>
                            <div className="font-serif text-3xl mt-1">
                                {sim.exhausted_at_year
                                    ? `Balance runs out at year ${sim.exhausted_at_year}.`
                                    : `Balance never runs out.`}
                            </div>
                        </div>
                        <span className={`chip ${sim.status === "Strong" ? "status-on-plan" : sim.status === "Monitor" ? "status-watch" : "status-review"}`}>
                            {sim.status}
                        </span>
                    </div>
                    <div style={{ height: 320 }}>
                        <ResponsiveContainer>
                            <LineChart data={sim.series}>
                                <XAxis dataKey="year" stroke="var(--muted)" tick={{ fontSize: 11 }} />
                                <YAxis stroke="var(--muted)" tick={{ fontSize: 11 }}
                                       tickFormatter={(v) => `$${(v / 1e6).toFixed(1)}M`} />
                                <Tooltip formatter={(v) => AUD(v)} labelFormatter={(l) => `Year ${l}`} />
                                <ReferenceLine y={0} stroke="var(--terracotta)" strokeDasharray="3 3" />
                                <Line type="monotone" dataKey="balance" stroke="var(--eucalyptus)"
                                      strokeWidth={2} dot={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            <SmsfComparator />
        </div>
    );
}

// ------------------------------------------------------------------
// SMSF vs Industry/Retail Super comparator (client-side projection)
// ------------------------------------------------------------------
// Models two 20-year balance paths starting from the same balance:
//   Path A — Industry / retail super:  balance-based fee only
//   Path B — SMSF:                     fixed annual admin + smaller % fee, plus one-off setup cost
// Assumes concessional contributions net of 15% super tax; same gross return.
function projectSuper({ start, years, contrib, gross, feePct, fixedFee }) {
    const series = [];
    let bal = start;
    let totalFees = 0;
    for (let y = 1; y <= years; y++) {
        const yearFixed = fixedFee || 0;
        bal = bal - yearFixed + contrib;
        const growth = bal * gross;
        bal += growth;
        const percentFee = bal * feePct;
        bal -= percentFee;
        totalFees += yearFixed + percentFee;
        series.push({ year: y, balance: Math.round(bal) });
    }
    return { series, endBalance: series[series.length - 1]?.balance || 0, totalFees: Math.round(totalFees) };
}

function SmsfComparator() {
    const [inputs, setInputs] = useState({
        start: 145000,
        years: 20,
        contrib: 25000,
        gross: 0.07,
        industryFeePct: 0.009,   // 0.9% pa — typical industry balanced option
        smsfFeePct: 0.002,       // 0.2% pa — platform/brokerage only
        smsfFixedFee: 2500,      // accountant + audit + ASIC review
        smsfSetup: 1000,         // one-off establishment
    });

    const update = (k, v) => setInputs(s => ({ ...s, [k]: Number(v) }));

    const { industry, smsf, chart, breakeven, verdict } = useMemo(() => {
        const industry = projectSuper({
            start: inputs.start, years: inputs.years, contrib: inputs.contrib,
            gross: inputs.gross, feePct: inputs.industryFeePct, fixedFee: 0,
        });
        const smsf = projectSuper({
            start: inputs.start - inputs.smsfSetup, years: inputs.years, contrib: inputs.contrib,
            gross: inputs.gross, feePct: inputs.smsfFeePct, fixedFee: inputs.smsfFixedFee,
        });
        // Combine into one chart series
        const chart = industry.series.map((row, i) => ({
            year: row.year,
            industry: row.balance,
            smsf: smsf.series[i].balance,
        }));
        // Find crossover point (year SMSF first overtakes industry, or vice-versa)
        let breakeven = null;
        for (let i = 0; i < chart.length; i++) {
            const diff = chart[i].smsf - chart[i].industry;
            const prevDiff = i > 0 ? chart[i - 1].smsf - chart[i - 1].industry : diff;
            if (i > 0 && Math.sign(diff) !== Math.sign(prevDiff)) {
                breakeven = { year: chart[i].year, balance: chart[i].smsf };
                break;
            }
        }
        const gap = smsf.endBalance - industry.endBalance;
        const winner = gap > 0 ? "SMSF" : "Industry";
        const feeSaving = industry.totalFees - smsf.totalFees;
        const verdict = {
            winner, gap: Math.abs(gap), feeSaving,
            summary: gap > 0
                ? `Over ${inputs.years} years, an SMSF ends up ~${AUD(Math.abs(gap))} ahead — mainly because % fees on ${AUD(smsf.endBalance)} are much lower than on the retail fund. Break-even ${breakeven ? `at year ${breakeven.year}` : "in the first years"}.`
                : `Over ${inputs.years} years, the retail/industry fund wins by ~${AUD(Math.abs(gap))}. Your balance ${AUD(inputs.start)} isn't large enough to absorb the SMSF's fixed admin costs. Consider revisiting once balances cross ~$250K per member.`,
        };
        return { industry, smsf, chart, breakeven, verdict };
    }, [inputs]);

    // Prefilled Copilot prompt (auto-navigates to /copilot with query params —
    // works whether Copilot supports the param or not; user can also paste manually)
    const copilotPrompt = `I'm choosing between staying in an industry super fund vs starting an SMSF. Balance ${AUD(inputs.start)}, contributing ${AUD(inputs.contrib)}/yr, ${inputs.years} years to retirement, ${(inputs.gross*100).toFixed(1)}% gross return. Industry fee 0.9% pa. SMSF fixed admin $2.5K pa + 0.2% pa platform. Over ${inputs.years} years my model shows ${verdict.winner} wins by ${AUD(verdict.gap)}. Give me a plain-English take covering the non-financial factors (control, complexity, insurance, ATO obligations) I should weigh alongside fees.`;

    const FieldPct = ({ k, label }) => (
        <div>
            <div className="label-eyebrow mb-2">{label}</div>
            <input type="number" step={0.05} value={(inputs[k] * 100).toFixed(2)}
                   onChange={e => update(k, Number(e.target.value) / 100)}
                   data-testid={`smsf-cmp-${k}`}
                   className="w-full border border-[var(--line)] px-3 py-2 rounded text-sm font-mono-num" />
        </div>
    );
    const FieldNum = ({ k, label, step = 1000 }) => (
        <div>
            <div className="label-eyebrow mb-2">{label}</div>
            <input type="number" step={step} value={inputs[k]}
                   onChange={e => update(k, e.target.value)}
                   data-testid={`smsf-cmp-${k}`}
                   className="w-full border border-[var(--line)] px-3 py-2 rounded text-sm font-mono-num" />
        </div>
    );

    return (
        <div className="mt-10 hairline bg-white p-6 md:p-8 rounded" data-testid="smsf-comparator">
            <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="label-eyebrow mb-2 flex items-center gap-2">
                        <Scales size={14} /> Modeller
                    </div>
                    <h2 className="font-serif text-2xl md:text-3xl tracking-tight">Should we start an SMSF?</h2>
                    <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--muted)" }}>
                        Side-by-side projection: staying in an industry/retail super fund vs
                        running your own SMSF. Same gross return, different fee structures.
                        Contributions assumed to be already net of 15% super tax.
                    </p>
                </div>
                <span className={`chip ${verdict.winner === "SMSF" ? "status-on-plan" : "status-watch"}`}
                      data-testid="smsf-cmp-winner">
                    {verdict.winner} wins by {AUD(verdict.gap)}
                </span>
            </div>

            <div className="grid md:grid-cols-4 gap-4 mb-6">
                <FieldNum k="start" label="Current balance" step={5000} />
                <FieldNum k="contrib" label="Contribution / yr" step={1000} />
                <FieldNum k="years" label="Years" step={1} />
                <FieldPct k="gross" label="Gross return (%)" />
                <FieldPct k="industryFeePct" label="Industry fee (%)" />
                <FieldPct k="smsfFeePct" label="SMSF platform (%)" />
                <FieldNum k="smsfFixedFee" label="SMSF fixed admin / yr" step={250} />
                <FieldNum k="smsfSetup" label="SMSF setup (one-off)" step={250} />
            </div>

            <div style={{ height: 300 }} className="mb-6">
                <ResponsiveContainer>
                    <LineChart data={chart}>
                        <XAxis dataKey="year" stroke="var(--muted)" tick={{ fontSize: 11 }} />
                        <YAxis stroke="var(--muted)" tick={{ fontSize: 11 }}
                               tickFormatter={(v) => `$${(v / 1e6).toFixed(1)}M`} />
                        <Tooltip formatter={(v) => AUD(v)} labelFormatter={(l) => `Year ${l}`} />
                        {breakeven && (
                            <ReferenceLine x={breakeven.year} stroke="var(--terracotta)"
                                           strokeDasharray="3 3" label={{ value: `Crossover Y${breakeven.year}`, fontSize: 10, fill: "var(--terracotta)" }} />
                        )}
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Line type="monotone" dataKey="industry" name="Industry super" stroke="var(--terracotta)"
                              strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="smsf" name="SMSF" stroke="var(--eucalyptus)"
                              strokeWidth={2} dot={false} />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="border border-[var(--line)] rounded p-4" data-testid="smsf-cmp-industry-card">
                    <div className="label-eyebrow">Industry super — end balance</div>
                    <div className="font-serif text-2xl mt-2 font-mono-num">{AUD(industry.endBalance)}</div>
                    <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>
                        Total fees over {inputs.years} yrs: <span className="font-mono-num">{AUD(industry.totalFees)}</span>
                    </div>
                </div>
                <div className="border border-[var(--line)] rounded p-4" data-testid="smsf-cmp-smsf-card">
                    <div className="label-eyebrow">SMSF — end balance</div>
                    <div className="font-serif text-2xl mt-2 font-mono-num">{AUD(smsf.endBalance)}</div>
                    <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>
                        Total fees over {inputs.years} yrs: <span className="font-mono-num">{AUD(smsf.totalFees)}</span>
                        {verdict.feeSaving > 0 && (
                            <span className="text-[var(--eucalyptus)]"> · saves {AUD(verdict.feeSaving)}</span>
                        )}
                    </div>
                </div>
                <div className="rounded p-4" style={{ background: "var(--eucalyptus)", color: "var(--bone)" }}
                     data-testid="smsf-cmp-verdict">
                    <div className="label-eyebrow" style={{ color: "rgba(255,255,255,0.75)" }}>Model verdict</div>
                    <div className="font-serif text-2xl mt-2">{verdict.winner}</div>
                    <div className="text-xs mt-2 opacity-90">
                        by {AUD(verdict.gap)} after {inputs.years} yrs
                    </div>
                </div>
            </div>

            <div className="text-sm leading-relaxed mb-4" style={{ color: "var(--muted)" }}
                 data-testid="smsf-cmp-summary">
                {verdict.summary}
            </div>

            <Link to={`/copilot?prompt=${encodeURIComponent(copilotPrompt)}`}
                  className="inline-flex items-center gap-2 text-sm text-[var(--eucalyptus)] hover:underline"
                  data-testid="smsf-cmp-ask-copilot">
                <ChatCircle size={14} /> Ask the AI Copilot for a deeper take →
            </Link>

            <div className="mt-6 pt-4 border-t border-[var(--line)] text-xs" style={{ color: "var(--muted)" }}>
                <strong>Fine print:</strong> educational only, not personal financial advice.
                Assumes contributions are already net of 15% super contributions tax, no insurance
                premiums, and returns are constant. Real-world outcomes vary with market volatility,
                fee tiers, and ATO compliance costs. Consider getting licensed advice before switching.
            </div>
        </div>
    );
}
